import { useCallback, useState } from 'react';

import { toast } from 'sonner';

import { defaultJurisdiction } from './procedural-helpers';
import {
  ALLOWED_PROCEDURAL_MIME_TYPES,
  MAX_PROCEDURAL_FILE_SIZE_BYTES,
} from '../../../../../../../../backend/src/constants/procedural-timeline-limits';
import { UploadEligibilityResponse } from '../../../../../../../../backend/src/matter/procedural-timeline/dto/upload-eligibility.response.dto';
import { ProceduralJurisdiction } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import { useDeleteStagedProceduralFile } from '@/api/mutations/useDeleteStagedProceduralFile';
import { useStageProceduralFile } from '@/api/mutations/useStageProceduralFile';
import { useSubmitProceduralFile } from '@/api/mutations/useSubmitProceduralFile';
import { useGetProceduralStagedDuplicate } from '@/api/queries/useGetProceduralStagedDuplicate';
import { useGetProceduralTimelineEvents } from '@/api/queries/useGetProceduralTimelineEvents';
import { useGetProceduralTimelineUploadEligibility } from '@/api/queries/useGetProceduralTimelineUploadEligibility';
import { detectTimeZone } from '@/new/lib/time-zones';

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getBlockedReason(eligibility: UploadEligibilityResponse | undefined): string | null {
  if (!eligibility || eligibility.canUpload) return null;
  if (eligibility.blockedBy === 'extraction_in_flight') {
    return 'A timetable is currently being processed. Please wait for it to finish before uploading another.';
  }
  if (eligibility.blockedBy === 'pending_suggestions') {
    const count = eligibility.pendingSuggestionCount;
    return `Resolve the ${count} pending suggestion${count === 1 ? '' : 's'} before uploading another timetable.`;
  }
  return null;
}

interface UseProceduralUploadParams {
  matterId: string;
  enabled?: boolean;
  /** Fired after a successful submit (after the success toast). */
  onSubmitted?: () => void;
}

/**
 * Owns the full procedural-timetable upload flow: client-side validation,
 * staging to a temp S3 key, timezone/jurisdiction selection, a content-hash
 * duplicate pre-check, and final submit. Presentation lives in the trigger
 * (button or dropzone) and {@link ProceduralUploadDialog}; this hook is the
 * shared brain so the two entry points never drift.
 */
export function useProceduralUpload({ matterId, enabled = true, onSubmitted }: UseProceduralUploadParams) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [timeZone, setTimeZone] = useState<string>(() => detectTimeZone());
  const [jurisdiction, setJurisdiction] = useState<ProceduralJurisdiction>(() => defaultJurisdiction());

  const { data: eligibility, isLoading: isLoadingEligibility } = useGetProceduralTimelineUploadEligibility(
    matterId,
    enabled,
  );
  const { data: existingEvents } = useGetProceduralTimelineEvents(matterId);
  const hasExistingEvents = (existingEvents?.length ?? 0) > 0;

  const stageFile = useStageProceduralFile();
  const submitFile = useSubmitProceduralFile();
  const deleteStagedFile = useDeleteStagedProceduralFile();

  const stagedUploadId = stageFile.data?.uploadId ?? null;
  const isStaging = stageFile.isLoading;
  const isSubmitting = submitFile.isLoading;
  const isBusy = isStaging || isSubmitting;

  const { data: duplicateCheck } = useGetProceduralStagedDuplicate(
    matterId,
    stagedUploadId,
    selectedFile?.name ?? null,
  );
  const isDuplicate = duplicateCheck?.isDuplicate === true;
  const duplicateName = isDuplicate ? (duplicateCheck?.existingFileName ?? '') : null;

  const blockedReason = getBlockedReason(eligibility);
  const isDisabled = isLoadingEligibility || !eligibility?.canUpload;
  const stageError = stageFile.error;
  const submitLabel = isSubmitting ? 'Submitting...' : isStaging ? 'Uploading...' : 'Submit';

  const clearLocalState = () => {
    setSelectedFile(null);
    stageFile.reset();
    submitFile.reset();
  };

  // Cancel paths: discard the temp S3 object if staging succeeded. If staging is still
  // in flight or failed, there's nothing confirmed in S3 yet — lifecycle reaps anything
  // stray. Use clearLocalState directly after a successful commit (temp already moved).
  const cancelAndClear = () => {
    if (stagedUploadId && selectedFile) {
      deleteStagedFile.mutate({ matterId, uploadId: stagedUploadId, filename: selectedFile.name });
    }
    clearLocalState();
  };

  const acceptFile = useCallback(
    (file: File) => {
      if (file.size > MAX_PROCEDURAL_FILE_SIZE_BYTES) {
        toast.error(`File is too large. Maximum size is ${formatBytes(MAX_PROCEDURAL_FILE_SIZE_BYTES)}.`);
        return;
      }
      if (
        file.type &&
        !ALLOWED_PROCEDURAL_MIME_TYPES.includes(file.type as (typeof ALLOWED_PROCEDURAL_MIME_TYPES)[number])
      ) {
        toast.error('Unsupported file type. Please upload a PDF, DOC, or DOCX file.');
        return;
      }

      setSelectedFile(file);
      stageFile.mutate({ matterId, file });
    },
    [matterId, stageFile.mutate],
  );

  const handleSubmit = async () => {
    if (!selectedFile || !stagedUploadId) return;
    try {
      await submitFile.mutateAsync({
        matterId,
        uploadId: stagedUploadId,
        name: selectedFile.name,
        mimeType: selectedFile.type,
        size: selectedFile.size,
        timeZone,
        jurisdiction,
      });
      toast.success('Timetable uploaded. Extraction will begin shortly.');
      clearLocalState();
      onSubmitted?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upload timetable.';
      toast.error(message);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (isBusy) return;
    if (!open) cancelAndClear();
  };

  return {
    // state
    selectedFile,
    timeZone,
    setTimeZone,
    jurisdiction,
    setJurisdiction,
    // derived
    stagedUploadId,
    isStaging,
    isSubmitting,
    isBusy,
    isDisabled,
    blockedReason,
    stageError,
    submitLabel,
    duplicateName,
    hasExistingEvents,
    // actions
    acceptFile,
    handleSubmit,
    cancelAndClear,
    handleOpenChange,
  };
}

export type ProceduralUpload = ReturnType<typeof useProceduralUpload>;
